import React, { useEffect, useRef, useState } from 'react';
import styles from './TripEmissionLog.module.scss';
import Container from '@/components/ui/Container/Container.tsx';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import MainLayout from '@/layouts/MainLayout/MainLayout.tsx';
import { useNavigate } from 'react-router';
import routes from '@/constants/routes.ts';
import { ContentWrapper } from '@/pages/ClimateProDashboard/ClimateProDashboard.tsx';
import ArrowRightIcon from '@/assets/Icons/Arrow-Right.svg?react';
import Card from '@/components/ui/Card/Card.tsx';
import LuggageIcon from '@/assets/Icons/Luggage-2.svg?react';
import AirplaneImage from '@/assets/Images/Airplane.png';
import PillowImage from '@/assets/Images/Pillow.png';
import FuelImage from '@/assets/Images/Fuel.png';
import Image from '@/components/ui/Image/Image.tsx';
import classNames from 'classnames';
import LogForm from '@/pages/MyClimate/TripEmissionLog/LogForm.tsx';
import TripEmissionLogFlyout from '@/pages/MyClimate/TripEmissionLog/TripEmissionLogFlyout.tsx';
import Tooltip from '@/components/ui/Tooltip/Tooltip.tsx';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys.ts';
import { getUncompliantTripsNum } from '@/api/dataCompliance.ts';
import { getTravelLogHistory } from '@/api/myClimate.ts';
import { useSearchParams } from 'react-router-dom';
import breakpoints from '@/constants/breakpoints.ts';
import Modal from '@/components/ui/Modal/Modal.tsx';
import { useModalStore } from '@/store/useModalStore.ts';
import CheckCircleIcon from '@/assets/Icons/Check-Circle.svg?react';
import TravelLogType = App.Enums.TravelLogType;
import { trackEvent } from '@/utils/productAnalyticsUtil.ts';
import { productAnalyticsEvents } from '@/constants/productAnalyticsEvents.ts';
import { actionNeededDefaultPeriod, isDataCompliancePeriod } from '@/constants/dataCompliance.ts';
import Co2Period = App.Enums.Co2Period;
import DataComplianceTraveler from '@/pages/MyClimate/TripEmissionLog/DataComplianceTraveler.tsx';

const manualTripLogModal = 'manualTripLogModal';
const tripLogHowThisWorksModal = 'tripLogHowThisWorksModal';
const stepExitAnimationDurationMs = 140;
const stepEnterAnimationDurationMs = 180;
const historyBadgeAnimationDurationMs = 900;

type ModalStepAnimationState = 'idle' | 'exit' | 'hidden' | 'enter';

const manualTripModeLabels: Record<TravelLogType, string> = {
	flight: 'Flight',
	accommodation: 'Accommodation',
	mileage: 'Ground transport',
};
const TripEmissionLog: React.FC = () => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const requestedActionNeededPeriod = searchParams.get('period');
	const [selectedTripMode, setSelectedTripMode] = useState<TravelLogType | undefined>(undefined);
	const [activeMode, setActiveMode] = useState<'log new trip' | 'missing details'>('log new trip');
	const [manualTripCompleted, setManualTripCompleted] = useState(false);
	const [manualTripAnimationState, setManualTripAnimationState] = useState<ModalStepAnimationState>('idle');
	const [animateHistoryCount, setAnimateHistoryCount] = useState(false);
	const [actionNeededPeriod, setActionNeededPeriod] = useState<Co2Period>(
		isDataCompliancePeriod(requestedActionNeededPeriod) ? requestedActionNeededPeriod : actionNeededDefaultPeriod,
	);
	// Indicator underline
	const logRef = useRef<HTMLButtonElement>(null);
	const missingRef = useRef<HTMLButtonElement>(null);
	const indicatorRef = useRef<HTMLSpanElement>(null);
	const manualTripExitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const manualTripEnterTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const manualTripEnterFrameRef = useRef<number | null>(null);
	const historyBadgeAnimationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [indicatorLeft, setIndicatorLeft] = useState(0);
	const [indicatorWidth, setIndicatorWidth] = useState(0);
	const { openModal, closeModal } = useModalStore();

	const { data: last30DaysActionNeededTrips } = useQuery({
		queryKey: [queryKeys.dataComplianceNumOfTrips, actionNeededDefaultPeriod],
		queryFn: () => getUncompliantTripsNum({ period: actionNeededDefaultPeriod }),
	});

	const { data: travelLogHistoryCountData } = useQuery({
		queryKey: [queryKeys.travelLogHistory, 'count'],
		queryFn: () => getTravelLogHistory({ page: 1, perPage: 1 }),
	});

	const travelLogHistoryCount = travelLogHistoryCountData?.pagination.total || 0;

	const clearManualTripTransitionTimers = () => {
		if (manualTripExitTimeoutRef.current) {
			clearTimeout(manualTripExitTimeoutRef.current);
			manualTripExitTimeoutRef.current = null;
		}

		if (manualTripEnterTimeoutRef.current) {
			clearTimeout(manualTripEnterTimeoutRef.current);
			manualTripEnterTimeoutRef.current = null;
		}

		if (manualTripEnterFrameRef.current) {
			cancelAnimationFrame(manualTripEnterFrameRef.current);
			manualTripEnterFrameRef.current = null;
		}
	};

	const transitionManualTripModalContent = (updateContent: () => void) => {
		clearManualTripTransitionTimers();
		setManualTripAnimationState('exit');

		manualTripExitTimeoutRef.current = setTimeout(() => {
			setManualTripAnimationState('hidden');
			updateContent();

			manualTripEnterFrameRef.current = requestAnimationFrame(() => {
				setManualTripAnimationState('enter');

				manualTripEnterTimeoutRef.current = setTimeout(() => {
					setManualTripAnimationState('idle');
				}, stepEnterAnimationDurationMs);
			});
		}, stepExitAnimationDurationMs);
	};

	const triggerHistoryCountAnimation = () => {
		if (historyBadgeAnimationTimeoutRef.current) {
			clearTimeout(historyBadgeAnimationTimeoutRef.current);
		}

		setAnimateHistoryCount(true);
		historyBadgeAnimationTimeoutRef.current = setTimeout(() => {
			setAnimateHistoryCount(false);
		}, historyBadgeAnimationDurationMs);
	};

	const openManualTripModal = (tripMode: TravelLogType) => {
		clearManualTripTransitionTimers();
		setSelectedTripMode(tripMode);
		setManualTripCompleted(false);
		setManualTripAnimationState('idle');
		openModal(manualTripLogModal);
	};

	const handleManualTripSubmitted = () => {
		transitionManualTripModalContent(() => setManualTripCompleted(true));
	};

	const handleManualTripModalClose = () => {
		const shouldAnimateHistoryBadge = manualTripCompleted;

		clearManualTripTransitionTimers();
		setSelectedTripMode(undefined);
		setManualTripCompleted(false);
		setManualTripAnimationState('idle');

		if (shouldAnimateHistoryBadge) {
			triggerHistoryCountAnimation();
		}
	};

	useEffect(() => {
		const activeButton = activeMode === 'log new trip' ? logRef.current : missingRef.current;
		if (activeButton) {
			const rect = activeButton.getBoundingClientRect();
			const parentRect = activeButton.parentElement?.getBoundingClientRect();
			if (parentRect) {
				setIndicatorLeft(rect.left - parentRect.left);
				setIndicatorWidth(rect.width);
			}
		}
	}, [activeMode, last30DaysActionNeededTrips?.count]);

	useEffect(() => {
		setSelectedTripMode(undefined);
	}, [activeMode]);

	useEffect(() => {
		const mode = searchParams.get('mode');
		if (mode === 'missing-details') {
			setActiveMode('missing details');
			const period = searchParams.get('period');
			const nextPeriod = isDataCompliancePeriod(period) ? period : actionNeededDefaultPeriod;
			setActionNeededPeriod(nextPeriod);

			if (period !== nextPeriod) {
				setSearchParams({ mode: 'missing-details', period: nextPeriod }, { replace: true });
			}
		} else if (mode === 'log-new-trip') {
			setActiveMode('log new trip');
		} else {
			setActiveMode('log new trip');
			if (mode) {
				setSearchParams({});
			}
		}
	}, [searchParams, setSearchParams]);

	useEffect(() => {
		return () => {
			clearManualTripTransitionTimers();

			if (historyBadgeAnimationTimeoutRef.current) {
				clearTimeout(historyBadgeAnimationTimeoutRef.current);
			}
		};
	}, []);

	const handleModeChange = (mode: 'log new trip' | 'missing details') => {
		if (mode === activeMode) {
			return;
		}

		if (mode === 'missing details') {
			trackEvent(productAnalyticsEvents.myClimate.tripLog.actionNeeded.open, {
				postHogEventName: 'trip_log_action_needed_viewed',
				postHogProperties: { category: 'trip-log' },
			});
			setActionNeededPeriod(actionNeededDefaultPeriod);
		} else {
			trackEvent(productAnalyticsEvents.myClimate.tripLog.logTrip.open);
		}

		setActiveMode(mode);
		setSearchParams(
			mode === 'missing details'
				? { mode: 'missing-details', period: actionNeededDefaultPeriod }
				: { mode: 'log-new-trip' },
		);
	};

	const handleActionNeededPeriodChange = (period: Co2Period) => {
		setActionNeededPeriod(period);
		setSearchParams({ mode: 'missing-details', period }, { replace: true });
	};

	return (
		<MainLayout>
			<Container>
				<FlexBlock flexDirection="column" rowGap="32px" className={styles.tripEmissionLogWrapper}>
					<FlexBlock columnGap="6px" alignItems="center" className={styles.breadcrumb}>
						<button onClick={() => navigate(routes.myClimate)}>Dashboard</button>
						<span>
							<ArrowRightIcon />
						</span>
						<button>Trip Emission Log</button>
					</FlexBlock>

					<ContentWrapper>
						<div className={styles.tripEmissionLogMenuWrapper}>
							<FlexBlock
								justifyContent="space-between"
								flexDirection={{ [breakpoints.zero]: 'column', [breakpoints.tabletSmall]: 'row' }}
								rowGap="20px"
								className={styles.header}
							>
								<FlexBlock alignItems="center" columnGap="16px">
									<FlexBlock
										justifyContent="center"
										alignItems="center"
										className={styles.iconWrapper}
									>
										<LuggageIcon />
									</FlexBlock>
									<h1>Trip emission log</h1>
								</FlexBlock>
								<FlexBlock
									alignItems="center"
									columnGap="12px"
									rowGap="10px"
									className={styles.headerActions}
								>
									<div className={styles.infoButton}>
										<Tooltip
											showLearnMoreText
											learnMoreLabel="How this works"
											onLearnMoreClick={() => {
												openModal(tripLogHowThisWorksModal);
												trackEvent(productAnalyticsEvents.myClimate.tripLog.overview.help);
											}}
											text="Learn how to log new trips and review trips that need action."
										/>
									</div>
									<TripEmissionLogFlyout
										historyCount={travelLogHistoryCount}
										animateHistoryCount={animateHistoryCount}
										onHistoryChanged={triggerHistoryCountAnimation}
									/>
								</FlexBlock>
							</FlexBlock>
							<Card>
								<FlexBlock alignItems="center" columnGap="16px" className={styles.cardHeader}>
									<div className={styles.buttonWrapper}>
										<span
											ref={indicatorRef}
											className={styles.activeIndicator}
											style={{
												transform: `translateX(${indicatorLeft}px)`,
												width: `${indicatorWidth}px`,
											}}
										/>
										<button
											ref={logRef}
											onClick={() => handleModeChange('log new trip')}
											className={activeMode === 'log new trip' ? styles.active : ''}
										>
											Log new trip
										</button>
										<button
											ref={missingRef}
											onClick={() => handleModeChange('missing details')}
											className={activeMode === 'missing details' ? styles.active : ''}
										>
											Action needed
											{!!last30DaysActionNeededTrips?.count && (
												<span className={styles.notificationBadge}>
													{last30DaysActionNeededTrips.count}
												</span>
											)}
										</button>
									</div>
								</FlexBlock>
								{activeMode === 'log new trip' ? (
									<div>
										<div className={styles.subheader}>
											Add travel booked outside Goodwings so its emissions can be included in your
											organization’s reporting.
										</div>
										<div className={styles.buttonsWrapper}>
											<p className={styles.selectTitle}>What would you like to add?</p>
											<FlexBlock
												flexDirection={{
													[breakpoints.zero]: 'column',
													[breakpoints.tablet]: 'row',
												}}
												columnGap="48px"
												rowGap="20px"
											>
												<button
													className={classNames(styles.tripModeBttn, {
														[styles.selected]: selectedTripMode === 'flight',
													})}
													onClick={() => {
														openManualTripModal('flight');
														trackEvent(
															productAnalyticsEvents.myClimate.tripLog.logTrip.interact,
															{
																deduplicationKey: 'card.flight',
																postHogEventName: 'trip_log_flight_open',
																postHogProperties: { category: 'trip-log' },
															},
														);
													}}
												>
													<p>Flight</p> <Image src={AirplaneImage} fallbackSrc={''} />
												</button>
												<button
													className={classNames(styles.tripModeBttn, {
														[styles.selected]: selectedTripMode === 'accommodation',
													})}
													onClick={() => {
														openManualTripModal('accommodation');
														trackEvent(
															productAnalyticsEvents.myClimate.tripLog.logTrip.interact,
															{
																deduplicationKey: 'card.accommodation',
																postHogEventName: 'trip_log_accommodation_open',
																postHogProperties: { category: 'trip-log' },
															},
														);
													}}
												>
													<p>Accommodation</p> <Image src={PillowImage} fallbackSrc={''} />
												</button>
												<button
													className={classNames(styles.tripModeBttn, {
														[styles.selected]: selectedTripMode === 'mileage',
													})}
													onClick={() => {
														openManualTripModal('mileage');
														trackEvent(
															productAnalyticsEvents.myClimate.tripLog.logTrip.interact,
															{
																deduplicationKey: 'card.mileage',
																postHogEventName: 'trip_log_mileage_open',
																postHogProperties: { category: 'trip-log' },
															},
														);
													}}
												>
													<p>Ground transport</p> <Image src={FuelImage} fallbackSrc={''} />
												</button>
											</FlexBlock>
										</div>
									</div>
								) : (
									<div>
										<div className={styles.subheader}>Trips needing your input</div>
										<p className={classNames(styles.selectTitle, styles.actionNeededDescription)}>
											Add the missing details to keep your company’s CO₂ reporting accurate.
											<br />
											Review trips from the selected date range with missing accommodation or
											transportation details.
										</p>
										<DataComplianceTraveler
											timePeriod={actionNeededPeriod}
											setTimePeriod={handleActionNeededPeriodChange}
											onTravelLogHistoryAdded={triggerHistoryCountAnimation}
										/>
									</div>
								)}
							</Card>
						</div>

						<Modal
							name={manualTripLogModal}
							className={classNames(
								styles.dataComplianceLogModal,
								(selectedTripMode === 'flight' || selectedTripMode === 'mileage') &&
									styles.routeDropdownLogModal,
							)}
							onClose={handleManualTripModalClose}
						>
							{selectedTripMode && (
								<div className={styles.modalContent}>
									<div className={styles.modalHeader}>
										<h3>{manualTripCompleted ? 'Travel details saved' : 'Add travel details'}</h3>
										<div className={styles.modalTripData}>
											<span>{manualTripModeLabels[selectedTripMode]}</span>
											<span>Travel booked outside Goodwings</span>
										</div>
									</div>
									<div
										className={classNames(
											styles.modalStepTransition,
											manualTripCompleted && styles.modalStepCentered,
											manualTripAnimationState === 'exit' && styles.modalStepExit,
											manualTripAnimationState === 'hidden' && styles.modalStepHidden,
											manualTripAnimationState === 'enter' && styles.modalStepEnter,
										)}
									>
										{manualTripCompleted ? (
											<div className={styles.completionSuccess}>
												<CheckCircleIcon className={styles.completionSuccessIcon} />
												<p>The travel details were added to your emission log.</p>
												<button
													className={styles.completionCloseButton}
													onClick={() => closeModal(handleManualTripModalClose)}
												>
													Close
												</button>
											</div>
										) : (
											<LogForm
												compactDataCompliance
												tripMode={selectedTripMode}
												editingMode={false}
												viewMode={false}
												afterTravelLogSubmitting={handleManualTripSubmitted}
											/>
										)}
									</div>
								</div>
							)}
						</Modal>
						<Modal name={tripLogHowThisWorksModal} className={styles.tripLogInfoModal}>
							<h2 className={styles.modalTitle}>
								<span className={styles.modalTitleIcon} aria-hidden="true">
									<LuggageIcon />
								</span>
								How trip logging works
							</h2>
							<p>
								Use Log new trip when you traveled outside a Goodwings booking and need to add the trip
								yourself. You can add flights, accommodation, or ground transport so the emissions are
								included in your company’s CO₂ reporting.
							</p>
							<p>
								Action needed shows completed trips that are missing either accommodation or transport
								details. If you were the traveler or the person who booked the trip, you can add the
								missing information or confirm it was not needed.
							</p>
							<p>
								Your trip logs show your history of trip details added or updated from this page. You
								may be able to edit recent entries, depending on when they were submitted and your
								connection to the trip.
							</p>
						</Modal>
					</ContentWrapper>
				</FlexBlock>
			</Container>
		</MainLayout>
	);
};

export default TripEmissionLog;
